import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { sendEvent } from '../../../logic/analytics/analytics.utils';
import Incentive from '../incentive/incentive.component';
import Progress from '../progress/progress.component';
import { CallToAction as CallToActionPrimary } from '../callToAction/primary/callToAction.component';
import { CallToAction as CallToActionSecondary } from '../callToAction/secondary/callToAction.component';
import { MyListButtonSecondary } from '../myListCallToAction/myListCallToAction.component';
import { PodcastInformation } from '../podcast-information/podcastInformation.component';
import { PROGRAM_INFORMATION_CLOSED_SIGNAL } from '../podcast-information/podcastInformation.constants';
import { SELECTED_SIGNAL } from '../signals';
import { getLinesCount } from '../incentive/incentive.utils';
import { makeTextPatch } from '../utils/templates';
import { isVisibleCTA } from '../solo/solo.utils';
import { makeProgressBarProperties } from '../progress/progress.utils';
import { isVisibleInformationButton } from '../jumbotron/jumbotron.utils';
import {
  getProgramInformationFocusedStyle,
  getProgramInformationUnFocusedStyle,
} from '../podcast-information/podcastInformation.style';
import { getInitialStyle, defaultContext, getLeftContainerWidth, colors } from './jumbotronSquare.style';
import {
  imageOptions,
  sizes,
  templates,
  VERTICAL_PADDING,
  Z_INDEX_NORMAL,
  Z_INDEX_WITH_PROGRAM_INFO,
} from './jumbotronSquare.constants';

/**
 * The JumbotronSquare is a component that highlights podcasts and audiobooks programs. It is therefore used on the program page and on the clip page.
 *
 * @version 1.1.12
 * @see https://zeroheight.com/726d5c282/v/latest/p/93fecc-jumbotronsquare--v1112/b/3946b9
 */
export default class JumbotronSquare extends Lightning.Component {
  static _template() {
    return _merge(
      {
        FlexContainer: {
          Image: {
            shader: {
              type: Lightning.shaders.RoundedRectangle,
            },
          },
          ContainerD: {
            ContainerE: {
              Incentive: {
                type: Incentive,
              },
              Title: {},
              Author: {},
            },
            ContainerH: {
              ProgressBar: {
                type: Progress,
              },
              ButtonsContainer: {
                PlayButton: {
                  type: CallToActionPrimary,
                },
                MyListButton: {
                  type: MyListButtonSecondary,
                  passSignals: {
                    [SELECTED_SIGNAL]: true,
                  },
                },
                InfoButton: {
                  type: CallToActionSecondary,
                },
              },
            },
            Separator: { rect: true },
            ContainerI: {
              ExtraDetails: {},
              Description: {},
            },
          },
        },
        ProgramInformation: {
          type: PodcastInformation,
          signals: {
            [PROGRAM_INFORMATION_CLOSED_SIGNAL]: '_handleProgramInformationClose',
          },
        },
      },
      getInitialStyle(),
    );
  }

  static getBottomMargin({ screenSize }) {
    return VERTICAL_PADDING[screenSize];
  }

  _construct() {
    this._style = defaultContext;
    this._buttons = [];
    this._index = 0;
  }

  _detach() {
    this._buttons = null;
    this._focused = null;
  }

  get item() {
    return this._item;
  }

  set item({ content, blockIndex, itemIndex, config, colorScheme = defaultContext.colorMode }) {
    this._item = content;

    const { action, author, bookmark, description, extraDetails, image, incentives, progress, title } = this._item;
    // const incentives = [
    //   {
    //     display: {
    //       variant: 'primary',
    //     },
    //     lines: [
    //       {
    //         theme: 'text',
    //         text: 'Text',
    //       },
    //       {
    //         theme: 'stamp',
    //         text: 'Primary',
    //       },
    //     ],
    //   },
    //   {
    //     display: {
    //       variant: 'secondary',
    //     },
    //     lines: [
    //       {
    //         theme: 'stamp',
    //         text: 'Secondary',
    //       },
    //     ],
    //   },
    // ];
    const incentiveLines = getLinesCount(incentives);
    const leftWidth = getLeftContainerWidth(this._style.screenSize, this.tag('FlexContainer').x);
    const textColor = colors.text[this._style.colorMode];

    this.patch({
      FlexContainer: {
        Image: {
          src: image.src,
        },
        ContainerD: {
          w: leftWidth,
          ContainerE: {
            w: leftWidth,
            visible: incentiveLines > 0 || title || author,
            Incentive: {
              visible: incentiveLines > 0,
              data: {
                linesCount: incentiveLines,
                w: leftWidth,
                incentives,
                colorMode: this._style.colorMode,
              },
            },
            Title: makeTextPatch(title, leftWidth, textColor),
            Author: makeTextPatch(author, leftWidth, textColor),
          },
          ContainerH: {
            ProgressBar: {
              w: Math.round(leftWidth * 0.33),
              ...makeProgressBarProperties(progress),
            },
            ButtonsContainer: {
              PlayButton: {
                visible: isVisibleCTA(action),
                label: _get(action, 'label', ''),
                picto: _get(action, 'picto', ''),
              },
              MyListButton: {
                visible: Boolean(bookmark),
                item: bookmark,
                blockIndex,
                itemIndex,
              },
              InfoButton: {
                visible: isVisibleInformationButton(this._item),
              },
            },
          },
          Separator: {
            w: leftWidth,
            visible: extraDetails || description,
          },
          ContainerI: {
            ExtraDetails: makeTextPatch(extraDetails, leftWidth, textColor),
            Description: makeTextPatch(description, leftWidth, textColor),
          },
        },
      },
      ProgramInformation: {
        data: this._item,
      },
    });

    this._buttons = this.tag('ButtonsContainer').children.filter(item => item.visible);
    this._setIndex();
  }

  set contentPadding(value) {
    this.tag('FlexContainer').x = Number.isFinite(value) ? value : 0;
  }

  _setIndex(index = this._index) {
    this._index = index;
    this._focused = this._buttons && this._buttons[this._index];
  }

  _showProgramInformation() {
    this.zIndex = Z_INDEX_WITH_PROGRAM_INFO;
    this.tag('ProgramInformation').patch(getProgramInformationFocusedStyle());
    this._focused = this.tag('ProgramInformation');
  }

  _handleProgramInformationClose() {
    this.zIndex = Z_INDEX_NORMAL;
    this.tag('ProgramInformation').patch(getProgramInformationUnFocusedStyle());
    this._setIndex();
    sendEvent({ key: 'closeMoreInfo' });
  }

  _getFocused() {
    return this._focused;
  }

  _handleRight() {
    if (!this._buttons) {
      return false;
    }

    if (this._index < this._buttons.length - 1) {
      this._setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleLeft() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _handleEnter() {
    if (this._focused === this.tag('InfoButton')) {
      this._showProgramInformation();
      sendEvent({ key: 'pressMoreInfo' });
    } else {
      this.signal(SELECTED_SIGNAL, this._item);
    }
  }
}

JumbotronSquare.Templates = templates;
JumbotronSquare.Sizes = sizes;
JumbotronSquare.ImageOptions = imageOptions;
